// Foyer's replacement for nenda-autoplayer's src/lib/deviceController.ts.
//
// Ports the SINGLETON PATTERN, not just the interface: `DeviceController` is
// used two ways upstream - as a constructor-injected dependency of
// `autoplayerMachine` (only `rebootDevice()`/`logOut()`, called from
// `actions.ts::runCommands` - confirmed by direct grep, nothing else there
// touches it), AND called directly as `DeviceController.getInstance()` inside
// `Playback.tsx`/`VideoPlayer.tsx` for platform branching. Both call sites
// need to keep working unmodified once ported, hence the singleton rather
// than a plain object.
//
// `platform` is always `"web"` here: the ported Shaka/HTML video path never
// needs the Tizen/AVPlay branch upstream's `VideoPlayer.tsx` has - that
// decision is made OUTSIDE the ported engine, by Foyer's own existing
// provenance-based routing to `AvPlayLayer` (see the plan's §5). A ported
// `VideoPlayer.tsx` should have its Tizen branch deleted during that port,
// not kept alive by this controller pretending to be Tizen.
import { bridge } from "../lib/foyerBridge";

interface NetworkInformation {
  ip?: string;
  wifiSignalStrength?: number;
}

const NOT_AVAILABLE = "N/A";

class WebDeviceController {
  private static instance: WebDeviceController;

  public readonly platform = "web" as const;
  public readonly osVersion: string;
  public readonly deviceId: string;
  public readonly firmwareVersion: string;
  public readonly deviceModel: string;
  public readonly serialNumber: string;
  public readonly nativeAppVersion: string;

  private constructor() {
    // FoyerBridge has no OS-version concept distinct from firmware; Foyer's
    // heartbeat already reports device telemetry through its own path, so
    // this class only needs to satisfy the ported machine/components' own
    // reads, not be a second source of truth for it.
    this.osVersion = NOT_AVAILABLE;
    const info = this.handledCall(() => bridge.getDeviceInfo(), null);
    this.deviceId = info?.serial ?? NOT_AVAILABLE;
    this.firmwareVersion = info?.firmware ?? NOT_AVAILABLE;
    this.deviceModel = info?.model ?? NOT_AVAILABLE;
    this.serialNumber = info?.serial ?? NOT_AVAILABLE;
    this.nativeAppVersion =
      this.handledCall(() => bridge.appVersion(), null) ?? NOT_AVAILABLE;
  }

  public static getInstance(): WebDeviceController {
    if (!WebDeviceController.instance) {
      WebDeviceController.instance = new WebDeviceController();
    }
    return WebDeviceController.instance;
  }

  private handledCall<T>(fn: () => T, fallback: T): T {
    try {
      return fn();
    } catch (error) {
      console.error("[signage-engine] WebDeviceController call failed:", error);
      return fallback;
    }
  }

  /*
   * PUBLIC FUNCTIONS - the two `actions.ts::runCommands` actually calls.
   */

  /**
   * `FoyerBridge.rebootDevice()` returns whether the platform ACCEPTED the
   * request, not whether the device actually came back - same caveat as
   * every other bridge call. Upstream's own `rebootDevice()` is also
   * fire-and-forget (`void`), so this matches that contract; the fallback
   * mirrors what SignageSurface's other reboot-adjacent code already does
   * when the bridge can't act.
   */
  public rebootDevice(): void {
    const accepted = this.handledCall(() => bridge.rebootDevice(), false);
    if (!accepted) location.reload();
  }

  /**
   * A no-op here, deliberately: Foyer has no expo-router login flow for this
   * to replace, and an `unpair` command already runs through `App.tsx`'s own
   * `handleEvent` regardless of which signage engine is mounted - see the
   * plan's finding that command execution is already role-agnostic.
   */
  public logOut(): void {
    /* no-op - see doc comment */
  }

  /*
   * PUBLIC FUNCTIONS - read by `Playback.tsx`/`VideoPlayer.tsx` upstream for
   * platform branching and the schedule client's device-metadata header.
   * That header is dead once `FoyerScheduleClient` overrides
   * `getScreenSchedule()` entirely (the plan's §2) - Foyer's heartbeat
   * already reports device telemetry through its own untouched path - so
   * these two can ship degraded/best-effort rather than needing new
   * `FoyerBridge` surface area.
   */

  public getAvailableMemory(): number {
    return -1;
  }

  public getNetworkInformation(): NetworkInformation | undefined {
    const n = this.handledCall(() => bridge.getNetworkInfo(), null);
    if (!n) return undefined;
    return {
      ip: n.wifiIp ?? n.ethIp,
      wifiSignalStrength: n.wifiSignal,
    };
  }
}

export default WebDeviceController;
